import { useRouter } from "@/lib/router-helpers"
import { useMemo, useState, useEffect } from "react"
import ProgressPill from "@/components/progress-pill"
import WeekStrip from "@/components/week-strip"
import ScheduleHeader from "@/components/schedule-header"
import SlotsGridContainer from "@/components/slots-grid-container"
import SlotsSkeleton from "@/components/slots-skeleton"
import BookingSummary from "@/components/booking-summary"
import SchedulePageSkeleton from "@/components/schedule-page-skeleton"
import { useTranslation } from 'react-i18next';
import { Suspense } from "react"
import { useBookingStore } from "@/store/booking-store"
import { useServerTime } from "@/utils/server-time"

export default function SchedulePage({ mode = "booking" }: { mode?: "booking" | "reschedule" }) {
  const router = useRouter()
  const { t, i18n } = useTranslation();
  const { serverDate, serverDateYmd, hasServerTime, isLoading: isServerTimeLoading, isRetrying } = useServerTime()
  const { 
    selectedBranch, 
    selectedService, 
    selectedDate, 
    appointmentId: storeAppointmentId,
    setBookingState
  } = useBookingStore()

  useEffect(() => {
    if (mode === 'booking' && (!selectedBranch || !selectedService)) {
      router.push('/')
    }
  }, [mode, selectedBranch, selectedService, router])

  // Initialize selectedDate if not present
  useEffect(() => {
    if (!selectedDate) {
      setBookingState({ selectedDate: serverDateYmd })
    }
  }, [selectedDate, setBookingState, serverDateYmd])

  const branchId = selectedBranch?.id || ""
  const serviceId = selectedService?.id || ""
  const appointmentId = storeAppointmentId || ""
  const appointmentDate = selectedDate || serverDateYmd

  const [calendarType, setCalendarType] = useState<'GC' | 'EC'>(i18n.language === 'am' || i18n.language === 'oro' ? 'EC' : 'GC')

  useEffect(() => {
    const newCalendarType = i18n.language === 'am' || i18n.language === 'oro' ? 'EC' : 'GC';
    setCalendarType(newCalendarType);
    setBookingState({ calendarType: newCalendarType });
  }, [i18n.language, setBookingState]);

  const today = useMemo(() => serverDate, [serverDate])

  if (mode === 'booking' && (!selectedBranch || !selectedService)) {
    return null // or skeleton
  }

  if (!hasServerTime && (isServerTimeLoading || isRetrying)) {
    return (
      <div className="w-full mx-auto md:px-4 md:pb-24 pt-2">
        <SchedulePageSkeleton />
        <p className="text-sm text-gray-500 mt-3 px-2">{t("Syncing server time...")}</p>
      </div>
    )
  }

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
        }
        .animate-pulse-soft {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .7; }
        }
      `}</style>

      <div className="w-full mx-auto md:px-4 md:pb-24 pt-2 font-sans animate-fade-in-up">
        <div className="hidden md:block mb-6">
          <ProgressPill value={60} />
        </div>

        <h2 className="text-lg font-bold text-gray-900 mb-4 px-1">
          {mode === "reschedule" ? t("rescheduleAppointment") : t("selectDateAndTime")}
        </h2>

        <div className="rounded-3xl border border-gray-100 bg-white py-5 px-2 md:p-8 shadow-sm relative transition-all duration-300 hover:shadow-md">
          <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-40" />
          </div>
          
          <div className="relative z-10">
            <ScheduleHeader 
              branchId={branchId} 
              serviceId={serviceId} 
              appointmentDate={appointmentDate} 
              today={today} 
              calendarType={calendarType}
              setCalendarType={setCalendarType}
            />

            <div className="mt-6">
              <WeekStrip 
                branchId={branchId} 
                serviceId={serviceId} 
                initialDate={appointmentDate} 
                today={today} 
                calendarType={calendarType}
              />
            </div>

            <div className="flex-1 border-t border-dashed border-gray-200 md:my-8 mt-3" />

            <Suspense key={appointmentDate} fallback={<SlotsSkeleton />}>
              <SlotsGridContainer
                branchId={branchId}
                branchName={selectedBranch?.name}
                serviceId={serviceId}
                appointmentDate={appointmentDate}
                calendarType={calendarType}
              />
            </Suspense>
          </div>
        </div>

        <BookingSummary mode={mode} appointmentId={appointmentId} />
      </div>
    </>
  )
}
